import React from 'react'
import { CreativeCard } from 'pages/business/creatives/CreativeCard/CreativeCard'
import { formTypeWire } from 'pages/business/lead-cards/lead-card-preview/LeadCardPreview.types'
import { useState } from 'react'
import { creativeLetter, getCreativesFromGroup } from 'app/util'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import BrokenImageIcon from '@mui/icons-material/BrokenImage'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { useHistory } from 'react-router-dom'
import { useGetBusinessID } from '@marketing-milk/frontend'
import { AdCreativeGroup } from 'pages/business/campaigns/forms/CampaignForm'
import { Button } from '@mui/material'

export interface AdSummaryProps {
  selectedCreatives?: AdCreativeGroup[]
  sprintId?: number
  requiresLeadForm?: boolean
}

export function AdSummary({
  selectedCreatives,
  sprintId,
  requiresLeadForm = false,
}: AdSummaryProps) {
  const [activeCreativesIndex, setActiveCreativesIndex] = useState(0)
  const history = useHistory()
  const businessID = useGetBusinessID()
  const activeCreatives = getCreativesFromGroup(activeCreativesIndex, selectedCreatives).filter(
    (c): c is NonNullable<typeof c> => c !== null && c !== undefined
  )
  const currentAdCreativeGroup = activeCreativesIndex + 1
  const previousAdCreativeGroup = () => setActiveCreativesIndex(activeCreativesIndex - 1)
  const nextAdCreativeGroup = () => setActiveCreativesIndex(activeCreativesIndex + 1)

  const handleEditCreative = (id: number) => {
    const baseRoute = sprintId ? 'sprints' : 'creatives'
    const params = new URLSearchParams()
    if (sprintId) {
      params.append('sprintId', sprintId.toString())
      // The editor opens over the creatives tab only, whichever tab this summary sits on.
      params.append('tab', 'creatives')
    }
    params.append('ad', id.toString())
    history.push(`/${businessID}/${baseRoute}?${params.toString()}`)
  }

  const noCreative = (
    <div className="h-100 d-flex justify-content-center">
      <div className="center-vertical text-center">
        <BrokenImageIcon
          color="disabled"
          className="mx-auto mb-2"
          fontSize="large"
          style={{ fontSize: '40px' }}
        />
        <Typography variant="h5" className="MuiTypography-colorTextSecondary">
          No Creative Selected
        </Typography>
      </div>
    </div>
  )

  return (
    <div data-testid="ad-summary" className="w-100">
      <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,20rem),1fr))] gap-4">
        {activeCreatives.length > 0
          ? activeCreatives.map((creative, i) => (
              <div key={creative.id} className="flex min-w-0 flex-col gap-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-mm-neutral-1-600">
                    Creative {creativeLetter(i)}
                  </span>
                  <Button
                    size="small"
                    variant="text"
                    aria-label={`Edit ${creative.name || 'creative'}`}
                    onClick={() => handleEditCreative(creative.id)}
                  >
                    Edit
                  </Button>
                </div>
                <div className="min-w-0 flex-1">
                  <CreativeCard
                    creative={creative}
                    tools={false}
                    onSelect={() => handleEditCreative(creative.id)}
                  />
                </div>
                {creative.leadCardUsed ? (
                  <p className="px-1 text-xs text-mm-neutral-1-600">
                    <span className="font-semibold">Lead form: </span>
                    {creative.leadCardUsed.formName}
                    {creative.leadCardUsed.formType && (
                      <span> · {formTypeWire[creative.leadCardUsed.formType]}</span>
                    )}
                  </p>
                ) : requiresLeadForm ? (
                  <p className="px-1 text-xs font-medium text-mm-warning-800">
                    Lead form required — assign one before publishing.
                  </p>
                ) : null}
              </div>
            ))
          : noCreative}
      </div>
      {selectedCreatives && selectedCreatives?.length > 1 && (
        <div data-testid="ad-group-controls" className="w-100 mt-4">
          <div className="d-flex fit-content mx-auto text-center">
            <IconButton
              data-testid="previous-ad-group"
              aria-label="Previous ad group"
              size="medium"
              disabled={activeCreativesIndex === 0}
              onClick={previousAdCreativeGroup}
            >
              <ArrowBackIosIcon fontSize="small" />
            </IconButton>
            <Typography className="mx-4 center-vertical" variant="subtitle1">
              Ad group {currentAdCreativeGroup} of {selectedCreatives.length}
            </Typography>
            <IconButton
              data-testid="next-ad-group"
              aria-label="Next ad group"
              size="medium"
              disabled={currentAdCreativeGroup === selectedCreatives.length}
              onClick={nextAdCreativeGroup}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      )}
    </div>
  )
}
